import { LGraphNode, LiteGraph } from "litegraph.js";
import { v4 as uuidv4 } from 'uuid';
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

export class TextRenderNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Text Render";

    if (this.id === -1 || this.id == null) {
      this.id = uuidv4();
    }

    this.properties = {
      text: this.getUrlParam("text") || "Sample Text",
      font: this.getUrlParam("font") || "Arial",
      fontSize: parseInt(this.getUrlParam("fontSize")) || 200,
      lineHeight: 1.1,
      canvasWidth: 1080,
      canvasHeight: 1080,
    };

    // Create an offscreen canvas for the text
    this.textCanvas = document.createElement("canvas");
    this.textCanvas.width = this.properties.canvasWidth;
    this.textCanvas.height = this.properties.canvasHeight;
    this.textCtx = this.textCanvas.getContext("2d");

    // Regular inputs
    this.addInput("Text", "string", { multi_input: true });
    this.addInput("Font", "string");

    // Output
    this.addOutput("TextCanvas", "canvas");

    // Drivable slider — input dot sits on the slider
    addDrivableSlider(this, "Font Size", "fontSize", this.properties.fontSize, 0.5, 1000, 1);
    addDrivableSlider(this, "Line Height", "lineHeight", this.properties.lineHeight, 0.5, 3, 0.1);

    // Position drivable inputs at their widgets (2 visible inputs: Text, Font)
    finalizeDrivableSliders(this, 2);

    // Node dimensions
    this.size = [200, 94];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";

  }

  getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  updateUrlParam() {}

  onExecute() {
    // Sync drivable slider inputs (Font Size animates when driven)
    syncDrivableSliders(this);

    const incomingText = this.getInputData(0);
    if (Array.isArray(incomingText)) {
      this.properties.text = incomingText.join(" ");
    } else if (incomingText) {
      this.properties.text = incomingText;
    }

    const incomingFont = this.getInputData(1);
    if (incomingFont) {
      this.properties.font = incomingFont;
    }

    // Only redraw when something actually changed
    const sig = `${this.properties.text}|${this.properties.font}|${this.properties.fontSize}|${this.properties.lineHeight}`;
    if (sig !== this._lastSig) {
      this._lastSig = sig;
      this.drawText();
    }
    // Attach bounds to the canvas so downstream nodes can use them
    this.textCanvas.textBounds = this.textBounds;
    this.setOutputData(0, this.textCanvas);
  }

  drawText() {
    const { text, font, fontSize, canvasWidth, canvasHeight } = this.properties;

    this.textCtx.clearRect(0, 0, canvasWidth, canvasHeight);
    this.textCtx.font = `${fontSize}px ${font}`;
    this.textCtx.fillStyle = "#000";

    // Use real font metrics for accurate centering
    const sampleMetrics = this.textCtx.measureText("Mg");
    const ascent = sampleMetrics.actualBoundingBoxAscent || fontSize * 0.8;
    const descent = sampleMetrics.actualBoundingBoxDescent || fontSize * 0.2;

    const lines = text.split("\n");
    const lineHeight = fontSize * (this.properties.lineHeight || 1.1);
    const blockHeight = ascent + descent + (lines.length - 1) * lineHeight;
    const firstBaselineY = (canvasHeight - blockHeight) / 2 + ascent;

    let minX = canvasWidth, maxX = 0;
    lines.forEach((line, idx) => {
      const metrics = this.textCtx.measureText(line);
      const xPos = (canvasWidth - metrics.width) / 2;
      const yPos = firstBaselineY + idx * lineHeight;
      this.textCtx.fillText(line, xPos, yPos);
      if (xPos < minX) minX = xPos;
      if (xPos + metrics.width > maxX) maxX = xPos + metrics.width;
    });

    // Store text bounds so downstream nodes can skip expensive pixel scanning
    const topY = firstBaselineY - ascent;
    const bottomY = firstBaselineY + (lines.length - 1) * lineHeight + descent;
    this.textBounds = { x: minX, y: topY, w: maxX - minX, h: bottomY - topY };
  }
}

LiteGraph.registerNodeType("custom/TextRender", TextRenderNode);
